import {useRef, useState, useCallback, useEffect} from "react";
import routes from "../../../Routes/routes";
import {matchPath, useHistory, useLocation} from "react-router";
import {validateField} from "../../../utils/validation";
import {mapInitialReduxActionsToContainer} from "../../../utils/server";
import {trackClickEvent} from "../../../utils/utils";
import {setNetworkCallPending} from "../../Layout/actions";
import {setTrackingDataAction} from "../../../containers/RegistrationLoginPage/actions";
import {REGISTRATION_LOGIN} from "../../../types";

const HOVER_THROTTLE_FACTOR_IN_MS = 100;

export const useRippleEffect = (enableRipple, rippleDuration) => {
    const [rippleState, setRippleState] = useState([]);
    const ref = useRef(null);
    const timeRef = useRef(null);
    useEffect(() => {
        return () => {
            clearTimeout(timeRef.current);
        };
    }, []);

    const createRipple = useCallback(
        (event) => {
            if (ref.current && enableRipple) {
                const {width, height, left, top} = ref.current.getBoundingClientRect();
                const size = Math.max(width, height);
                const position = {
                    x: event.clientX - left - size / 2,
                    y: event.clientY - top - size / 2,
                };
                setRippleState({size, position});
                clearTimeout(timeRef.current);
                timeRef.current = setTimeout(() => {
                    setRippleState({});
                }, rippleDuration);
            }
        },
        [enableRipple, rippleDuration],
    );

    return {rippleState, ref, createRipple};
};
export const useDelayNavigation = () => {
    const history = useHistory();
    const location = useLocation();
    const {pathname} = location;
    const [inProgress, setInProgress] = useState(false);
    const timerRef = useRef(0);
    const promiseRef = useRef({cancel: () => {}});
    const delayNavigation = useCallback(
        (linkHref, event, dispatch, onPathChange) => {
            let connection =
                navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            const isFastConnection =
                typeof connection === "object" &&
                connection.effectiveType === "4g" &&
                connection.saveData === false;
            if (isFastConnection) {
                const activeRoute = routes.find((route) => matchPath(linkHref, route));
                const considerInstantExperience =
                    activeRoute?.path !== "*" &&
                    (!validateField(activeRoute, "object", "considerInstantExperience") ||
                        activeRoute.considerInstantExperience);
                if (considerInstantExperience) {
                    event.preventDefault();
                } else {
                    if (!inProgress && onPathChange) onPathChange();
                }
                if (considerInstantExperience && !inProgress) {
                    setInProgress(true);
                    activeRoute.component.load().then((component) => {
                        let initialDataRequestHandler = "";
                        if (validateField(activeRoute, "object", "extraParams.hooksEnabled")) {
                            initialDataRequestHandler = mapInitialReduxActionsToContainer(
                                activeRoute?.globalLayoutProps?.reduxBucketName ??
                                    activeRoute.componentName,
                            );
                        } else {
                            initialDataRequestHandler = component.default.requestInitialData;
                        }
                        const req = {};
                        req.path = linkHref;
                        promiseRef.current = dispatch(
                            initialDataRequestHandler("false", {
                                serverRequest: req,
                            }),
                        );
                        let isFulfilled = false;
                        let timerFulfilled = false;
                        timerRef.current = setTimeout(() => {
                            timerFulfilled = true;
                            dispatch(
                                setNetworkCallPending({
                                    status: true,
                                    promiseObj: promiseRef.current.promiseObj,
                                }),
                            );
                            promiseRef.current = {cancel: () => {}};
                            if (!isFulfilled) {
                                if (onPathChange) onPathChange();
                                history.push(linkHref);
                            }
                        }, 200);
                        Promise.resolve(promiseRef.current.promiseObj).then(() => {
                            isFulfilled = true;
                            dispatch(
                                setNetworkCallPending({
                                    status: false,
                                    promiseObj: Promise.resolve(),
                                }),
                            );
                            clearTimeout(timerRef.current);
                            promiseRef.current = {cancel: () => {}};
                            if (!timerFulfilled) {
                                if (onPathChange) onPathChange();
                                history.push(linkHref);
                            }
                        });
                    });
                }
            } else {
                if (onPathChange) onPathChange();
            }
        },
        [history, inProgress],
    );
    useResetEffect(inProgress, setInProgress, 400);
    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current);
            promiseRef.current.cancel();
            promiseRef.current = {cancel: () => {}};
            setInProgress(false);
        };
    }, [pathname]);
    return delayNavigation;
};

export const useMouseHoverHandle = () => {
    const [isHovered, setIsHovered] = useState(false);
    const location = useLocation();
    const {pathname} = location;
    const timerRef = useRef(0);
    const mouseHoverHandle = useCallback(
        (linkHref, dispatch, preventDefaultVal = false) => {
            let isServiceWorkerActivated =
                "serviceWorker" in navigator &&
                navigator.serviceWorker.controller !== null &&
                navigator.serviceWorker.controller.state === "activated";
            if (!preventDefaultVal && isServiceWorkerActivated && !isHovered) {
                clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setIsHovered(true);
                    const activeRoute = routes.find((route) => matchPath(linkHref, route));
                    if (
                        activeRoute?.path !== "*" &&
                        (!validateField(activeRoute, "object", "considerInstantExperience") ||
                            activeRoute.considerInstantExperience)
                    ) {
                        activeRoute.component.load().then((component) => {
                            let initialDataRequestHandler = "";
                            if (validateField(activeRoute, "object", "extraParams.hooksEnabled")) {
                                initialDataRequestHandler = mapInitialReduxActionsToContainer(
                                    activeRoute?.globalLayoutProps?.reduxBucketName ??
                                        activeRoute.componentName,
                                );
                            } else {
                                initialDataRequestHandler = component.default.requestInitialData;
                            }
                            const req = {};
                            req.path = linkHref;

                            dispatch(
                                initialDataRequestHandler("false", {
                                    serverRequest: req,
                                    skipDispatch: true,
                                }),
                            );
                        });
                    }
                }, HOVER_THROTTLE_FACTOR_IN_MS);
            }
        },
        [isHovered],
    );

    const mouseHoverOut = useCallback(() => {
        clearTimeout(timerRef.current);
    }, []);

    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current);
            setIsHovered(false);
        };
    }, [pathname]);
    useResetEffect(isHovered, setIsHovered, 20000);
    return {mouseHoverHandle, mouseHoverOut};
};

export const clickHandle = (event, paramsObj) => {
    const {
        linkHref,
        onClickHandle,
        eventCategory,
        eventAction,
        dispatch,
        preventDefaultVal,
        enableRipple = false,
        createRipple,
        isPWALink,
        delayNavigation,
        onPathChange,
        isSecureUrlBehindLogin = false,
        push,
        externalTrackingId = null,
    } = paramsObj;
    if (preventDefaultVal || isSecureUrlBehindLogin) {
        event.preventDefault();
    }

    if (isSecureUrlBehindLogin) {
        dispatch(
            setTrackingDataAction(
                {
                    trackingId: externalTrackingId,
                    ctaType: REGISTRATION_LOGIN,
                    referralURL: linkHref,
                    referralTitle: "External URL",
                },
                REGISTRATION_LOGIN,
            ),
        );
        push("/signup");
    }

    if (validateField(eventCategory) && validateField(eventAction)) {
        trackExternalClick(eventCategory, eventAction, linkHref);
    }
    if (enableRipple && !preventDefaultVal) {
        createRipple(event);
    }
    if (onClickHandle) onClickHandle();
    if (isPWALink && !preventDefaultVal) {
        delayNavigation(linkHref, event, dispatch, onPathChange);
    }
};

const trackExternalClick = (eventCategory, eventAction, linkHref) => {
    trackClickEvent(eventCategory, eventAction, linkHref);
};

export const usePreFetch = () => {
    const [isHovered, setIsHovered] = useState(false);
    const ref = useRef(null);
    const location = useLocation();
    const {pathname} = location;
    const timerRef = useRef(0);
    const preloadHandle = useCallback(
        (preventDefaultVal, linkHref) => {
            timerRef.current = setTimeout(() => {
                if (!preventDefaultVal && !isHovered) {
                    const prefetcher = document.createElement("link");
                    ref.current = prefetcher;
                    prefetcher.rel = "prefetch";
                    prefetcher.href = linkHref;
                    document.head.appendChild(prefetcher);
                    setIsHovered(true);
                }
            }, HOVER_THROTTLE_FACTOR_IN_MS);
        },
        [isHovered],
    );

    const mouseHoverOut = useCallback(() => {
        clearTimeout(timerRef.current);
    }, []);

    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current);
            setIsHovered(false);
            validateField(ref.current) && ref.current.remove();
        };
    }, [pathname]);
    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current);
        };
    }, [isHovered]);

    useResetEffect(isHovered, setIsHovered, 10000);
    return {preloadHandle, mouseHoverOut};
};

const useResetEffect = (isActive, setIsActive, timerInMS) => {
    useEffect(() => {
        if (isActive) {
            const timer = setTimeout(() => {
                setIsActive(false);
            }, timerInMS);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isActive, setIsActive, timerInMS]);
};
